import { defineStore } from 'pinia';
import { ref } from 'vue';
import DepartmentService from '@/services/hrm/departmentService';
import { toast } from '@/utils/toast';
import { createScopedCache } from './_cache';
import { MODULE_KEYS } from '@/config/modules-registry';
import { guardPlanModule } from '@/utils/planAccess';

const departmentListCache = createScopedCache('departments:list');
const departmentDetailCache = createScopedCache('departments:detail');
const DEPARTMENTS_LIST_TTL = 5 * 60 * 1000;
const DEPARTMENTS_DETAIL_TTL = 5 * 60 * 1000;

export const useDepartmentStore = defineStore('department', () => {
  const departments = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchDepartments(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      departments.value = [];
      return;
    }

    loading.value = true;
    error.value = null;
    const { force, ...query } = params;
    try {
      await departmentListCache.get(query, {
        ttlMs: DEPARTMENTS_LIST_TTL,
        force: force === true,
        loader: async () => {
          const response = await DepartmentService.getAll(query);
          return { departments: response.data || [] };
        },
        onHydrate: ({ departments: rows }) => {
          departments.value = rows || [];
        },
      });
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch departments';
      toast(error.value, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function ensureDepartments(params = {}) {
    return await fetchDepartments({ ...params, force: false });
  }

  async function createDepartment(departmentData) {
    loading.value = true;
    error.value = null;
    try {
      const response = await DepartmentService.create(departmentData);
      departmentListCache.clear();
      toast('Department created successfully!');
      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create department';
      toast(error.value, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateDepartment(departmentData) {
    loading.value = true;
    error.value = null;
    try {
      const response = await DepartmentService.update(departmentData.id, departmentData);
      departmentListCache.clear();
      departmentDetailCache.invalidate({ id: Number(departmentData.id) || departmentData.id });
      toast('Department updated successfully!');
      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update department';
      toast(error.value, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDepartmentById(departmentId) {
    loading.value = true;
    error.value = null;
    try {
      const payload = await departmentDetailCache.get(
        { id: Number(departmentId) || departmentId },
        {
          ttlMs: DEPARTMENTS_DETAIL_TTL,
          loader: async () => {
            const response = await DepartmentService.getById(departmentId);
            return { department: response.data };
          },
        }
      );
      return payload.department;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch department';
      toast(error.value, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteDepartments(departmentIds) {
    loading.value = true;
    error.value = null;
    try {
      await Promise.all(departmentIds.map(id => DepartmentService.delete(id)));
      departmentListCache.clear();
      departments.value = departments.value.filter(dept => !departmentIds.includes(dept.id));
      toast('Departments deleted successfully!');
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete departments';
      toast(error.value, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function downloadReport(format = 'xlsx', params = {}) {
    try {
      const res = await DepartmentService.exportReport(format, params);
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.href = url;
      link.setAttribute(
        'download',
        `department-report-${new Date().toISOString().slice(0, 10)}.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to export departments';
      toast(msg, 'error');
      throw err;
    }
  }

  return {
    departments,
    loading,
    error,
    fetchDepartments,
    ensureDepartments,
    createDepartment,
    updateDepartment,
    fetchDepartmentById,
    deleteDepartments,
    downloadReport,
  };
});
